import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LoaderCircle, Send, Sparkles, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiFetch } from "../api";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
};

const greeting: ChatMessage = {
  role: "assistant",
  content:
    "你好，我可以帮你查询、新增和修改婚礼数据。你也可以直接粘贴供应商确认单或付款信息。",
};

type ResizeDirection = "left" | "top" | "corner";

export function AiAssistant({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([greeting]);
  const [pendingWriteMessages, setPendingWriteMessages] =
    useState<ChatMessage[]>();
  const [panelSize, setPanelSize] = useState<{
    width: number;
    height: number;
  }>();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const resizeCleanupRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open || !window.matchMedia("(max-width: 900px)").matches) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => () => resizeCleanupRef.current(), []);

  const startResize = (
    direction: ResizeDirection,
    event: ReactPointerEvent<HTMLSpanElement>,
  ) => {
    if (window.matchMedia("(max-width: 900px)").matches) return;
    const panel = panelRef.current;
    if (!panel) return;
    event.preventDefault();
    resizeCleanupRef.current();
    const startX = event.clientX;
    const startY = event.clientY;
    const { width: startWidth, height: startHeight } =
      panel.getBoundingClientRect();
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    const cursor =
      direction === "left"
        ? "ew-resize"
        : direction === "top"
          ? "ns-resize"
          : "nwse-resize";
    document.body.style.cursor = cursor;
    document.body.style.userSelect = "none";

    const move = (moveEvent: PointerEvent) => {
      const maxWidth = Math.max(320, window.innerWidth - 36);
      const maxHeight = Math.max(360, window.innerHeight - 100);
      const width = Math.min(
        maxWidth,
        Math.max(320, startWidth - (moveEvent.clientX - startX)),
      );
      const height = Math.min(
        maxHeight,
        Math.max(360, startHeight - (moveEvent.clientY - startY)),
      );
      setPanelSize({
        width: direction === "top" ? startWidth : width,
        height: direction === "left" ? startHeight : height,
      });
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      resizeCleanupRef.current = () => undefined;
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    resizeCleanupRef.current = stop;
  };

  useEffect(() => {
    messageListRef.current?.scrollTo({
      top: messageListRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, sending]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);

  const requestAi = async (history: ChatMessage[], confirmWrites: boolean) => {
    setSending(true);
    try {
      const result = await apiFetch<{
        message: string;
        toolCalls: number;
        confirmationRequired: boolean;
      }>(`/workspaces/${workspaceId}/ai/chat`, {
        method: "POST",
        body: JSON.stringify({ messages: history, confirmWrites }),
      });
      setMessages((current) => [
        ...current,
        { role: "assistant", content: result.message },
      ]);
      if (result.confirmationRequired && !confirmWrites)
        setPendingWriteMessages(history);
      else setPendingWriteMessages(undefined);
      if (confirmWrites && result.toolCalls > 0)
        await queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes(workspaceId),
        });
    } catch (error) {
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          content:
            error instanceof Error
              ? `请求没有完成：${error.message}`
              : "请求没有完成，请稍后重试。",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const send = () => {
    const content = draft.trim();
    if (!content || sending) return;
    const userMessage: ChatMessage = { role: "user", content };
    const history = [...messages.slice(1), userMessage].slice(-8);
    setPendingWriteMessages(undefined);
    setMessages((current) => [...current, userMessage]);
    setDraft("");
    void requestAi(history, false);
  };

  return (
    <div className={`ai-assistant ${open ? "open" : ""}`}>
      {open && (
        <section
          ref={panelRef}
          className="ai-chat-panel"
          role="dialog"
          aria-modal="false"
          aria-label="知禧 AI 助手"
          style={panelSize}
        >
          <span
            className="ai-resize-handle left"
            aria-hidden="true"
            onPointerDown={(event) => startResize("left", event)}
          />
          <span
            className="ai-resize-handle top"
            aria-hidden="true"
            onPointerDown={(event) => startResize("top", event)}
          />
          <span
            className="ai-resize-handle corner"
            aria-hidden="true"
            onPointerDown={(event) => startResize("corner", event)}
          />
          <header>
            <div>
              <span className="ai-title-icon">
                <Sparkles size={16} />
              </span>
              <span>
                <strong>知禧 AI</strong>
                <small>DeepSeek 数据助理</small>
              </span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="关闭 AI 助手"
            >
              <X size={18} />
            </button>
          </header>
          <div
            ref={messageListRef}
            className="ai-message-list"
            aria-live="polite"
          >
            {messages.map((message, index) => (
              <div className={`ai-message ${message.role}`} key={index}>
                {message.role === "assistant" ? (
                  <div className="ai-message-content">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        table: ({ children }) => (
                          <div className="ai-table-scroll">
                            <table>{children}</table>
                          </div>
                        ),
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <span>{message.content}</span>
                )}
              </div>
            ))}
            {sending && (
              <div className="ai-message assistant pending">
                <LoaderCircle size={15} />
                <span>正在理解并处理…</span>
              </div>
            )}
            {pendingWriteMessages && !sending && (
              <div
                className="ai-write-confirmation"
                role="group"
                aria-label="确认 AI 写入"
              >
                <strong>需要你的确认</strong>
                <span>
                  AI 已整理好写入操作。确认后才会真正修改当前婚礼项目。
                </span>
                <div>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => {
                      setPendingWriteMessages(undefined);
                      setMessages((current) => [
                        ...current,
                        {
                          role: "assistant",
                          content: "已取消本次写入，没有修改数据。",
                        },
                      ]);
                    }}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={() => void requestAi(pendingWriteMessages, true)}
                  >
                    确认执行
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="ai-composer">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" &&
                  !event.shiftKey &&
                  !event.nativeEvent.isComposing
                ) {
                  event.preventDefault();
                  send();
                }
              }}
              placeholder="输入问题，或粘贴确认单…"
              aria-label="发送给 AI 的内容"
              rows={3}
              disabled={sending}
            />
            <button
              type="button"
              onClick={send}
              disabled={sending || !draft.trim()}
              aria-label="发送"
            >
              {sending ? <LoaderCircle size={18} /> : <Send size={18} />}
            </button>
          </div>
          <footer>写入前需要确认 · AI 不能删除 · 每日 token 配额受限</footer>
        </section>
      )}
      <button
        className="ai-launcher"
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label={open ? "关闭 AI 助手" : "打开 AI 助手"}
        aria-expanded={open}
      >
        {open ? <X size={22} /> : <Sparkles size={23} />}
      </button>
    </div>
  );
}
